import { inject, Injectable } from '@angular/core';
import { FindAssetPurchasesBuyConfigInterface } from '@app/shared/interfaces/configurations/find-asset-purchases-buy-config.interface';
import { FindAssetPurchasesSellConfigInterface } from '@app/shared/interfaces/configurations/find-asset-purchases-sell-config.interface';
import { FindAssetWithdrawalsConfigInterface } from '@app/shared/interfaces/configurations/find-asset-withdrawals-config.interface';
import { AssetRateTypes } from '@interfaces/asset/enums/asset-rate-types.enum';
import { PurchaseOperationEnum } from '@interfaces/user/purchase/purchase.operation.enum.interface';
import { PurchaseResponseInterface } from '@interfaces/user/purchase/purchase.response.interface';
import { Big } from 'big.js';
import { BehaviorSubject, filter, map, Observable, take, tap } from 'rxjs';
import { v4 as UUID } from 'uuid';
import { AssetsService } from '../abstract-services/assets/assets.service';
import { PurchasesService } from '../abstract-services/purchases/purchases.service';
import { CRUDEvents } from '../abstract/abstract-crud.service';
import { AuthRefreshService } from '../auth/auth-refresh.service';
import { ConfigurationsService } from '../configurations/configurations.service';
import { ExtendedWallet } from '../wallets-manager/wallet.class';

export type PurchaseConfig =
  | FindAssetPurchasesBuyConfigInterface
  | FindAssetPurchasesSellConfigInterface
  | FindAssetWithdrawalsConfigInterface
  | null;

export enum OperationTypes {
  BUY = 'buy',
  SELL = 'sell',
  WITHDRAWAL = 'withdrawal',
  RECEIVE = 'receive',
}

export interface PurchaseResume {
  amountAsset: number;
  amountEuros: number;
  assetRate: number;
  assetName: string;
  assetSymbol: string;
  assetImage: string;
  feePercent: string;
  feeInEuros: number;
}

@Injectable({
  providedIn: 'root',
})
export class PurchaseManagerService {
  #assetsService = inject(AssetsService);
  #purchasesService = inject(PurchasesService);
  #authService = inject(AuthRefreshService);
  #configurationsService = inject(ConfigurationsService);

  onRate$ = this.#assetsService
    .getObservable(this.#assetsService.ASSET_RATE_SECTION, {
      arrayEmpty: false,
      null: false,
    })
    .pipe(
      map(Number),
      tap(() => this.rateInitialized.next(true))
    );

  onRateLoading$ = this.#assetsService.getLoading$(
    this.#assetsService.ASSET_RATE_SECTION
  );

  private _onPurchaseConfig$ = new BehaviorSubject<PurchaseConfig>(null);
  onPurchaseConfig$ = this._onPurchaseConfig$.asObservable();

  private _onPurchaseConfigLoading$ = new BehaviorSubject<boolean>(false);
  onPurchaseConfigLoading$ = this._onPurchaseConfigLoading$.asObservable();

  onPurchaseCreate$ = this.#purchasesService.onCreate$();
  purchaseLoading$ = this.#purchasesService.onLoading$(CRUDEvents.CREATE);
  purchaseError$ = this.#purchasesService.onError$(CRUDEvents.CREATE);

  private rateInitialized = new BehaviorSubject<boolean>(false);
  onRateInitialized$ = this.rateInitialized.asObservable();

  get _afterPurchaseConfigLoading$(): Observable<boolean> {
    return this.onPurchaseConfigLoading$.pipe(
      filter(t => !t),
      take(1)
    );
  }

  getRate(assetId: string, operationType: AssetRateTypes = null): void {
    this.#assetsService.getRate(
      assetId,
      operationType as unknown as AssetRateTypes
    );
  }

  async getPurchaseConfig(
    assetId: string,
    operationType: OperationTypes
  ): Promise<PurchaseConfig> {
    this._onPurchaseConfigLoading$.next(true);
    const configuration = await this.#configurationsService.getConfiguration(
      assetId,
      operationType
    );
    this._onPurchaseConfig$.next(configuration as PurchaseConfig);
    this._onPurchaseConfigLoading$.next(false);
    return configuration as PurchaseConfig;
  }

  cleanRate(): void {
    this.#assetsService.cleanRate();
    this.rateInitialized.next(false);
  }

  // prettier-ignore
  convertAmountEurosToAsset(
    eurAmount: number,
    assetRate: number,
    purchaseConfig: FindAssetPurchasesBuyConfigInterface | FindAssetPurchasesSellConfigInterface,
    // operationType: AssetRateTypes,
  ): number {

    const euros = new Big(eurAmount || 0);
    const rate = new Big(assetRate || 0);

    const feePercent = Number(purchaseConfig.fee);

    const fee = euros.mul(feePercent).round(2);
    const netAmount = euros.minus(fee);
    const total = netAmount
      .div(rate)
      .round(5, Big.roundDown)
      .toNumber();

    return total;
  }

  convertAmountAssetToEuros(
    assetAmount: number,
    assetRate: number,
    purchaseConfig:
      | FindAssetPurchasesBuyConfigInterface
      | FindAssetPurchasesSellConfigInterface
    //operationType: AssetRateTypes
  ): number {
    const amount = new Big(assetAmount || 0);
    const rate = new Big(assetRate || 0);

    const feePercent = Number(purchaseConfig.fee);

    const fee = amount.mul(feePercent);

    const total = amount.minus(fee).mul(rate).toNumber();

    return total;
  }

  generateResume(
    wallet: ExtendedWallet,
    amount: number,
    assetRate: number,
    purchaseConfig:
      | FindAssetPurchasesBuyConfigInterface
      | FindAssetPurchasesSellConfigInterface,
    operationType: AssetRateTypes
  ): PurchaseResume {
    const isBuyOperation = operationType === AssetRateTypes.BUY;

    const amountAsset = isBuyOperation
      ? this.convertAmountEurosToAsset(
          amount,
          assetRate,
          purchaseConfig
          //operationType
        )
      : amount;

    const amountEuros = isBuyOperation
      ? amount
      : this.convertAmountAssetToEuros(
          amountAsset,
          assetRate,
          purchaseConfig
          //operationType
        );

    const feePercent = String(purchaseConfig.fee);

    const feeInEuros = new Big(amountEuros).mul(feePercent).toNumber();

    const resume: PurchaseResume = {
      feePercent,
      feeInEuros,
      amountAsset,
      amountEuros,
      assetRate,
      assetName: wallet.asset.name,
      assetSymbol: wallet.asset.symbol,
      assetImage: wallet.asset.image,
    };

    return resume;
  }

  makeOperation(
    walletId: string,
    operation: PurchaseResume,
    operationType: AssetRateTypes
  ): void {
    const isBuyOperation = operationType === AssetRateTypes.BUY;

    const amount = isBuyOperation
      ? operation.amountEuros.toString()
      : operation.amountAsset.toString();

    const externalReference = UUID();

    const purchaseData: PurchaseResponseInterface = {
      operation: operationType as unknown as PurchaseOperationEnum,
      amount,
      externalReference,
      rate: operation.assetRate.toString(),
    };

    this.#purchasesService.makeOperation(
      this.#authService.user.id,
      walletId,
      purchaseData
    );
  }
}
